import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ConnectCard from "./ConnectCard";
import QrCodeCard from "./QrCodeCard";

// Photographer-only home dashboard. One-shot fetch of /photographer/dashboard
// gives us storage, earnings and expiring-soon photos in a single call.
export default function PhotographerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extending, setExtending] = useState(false);
  const [deletingExpired, setDeletingExpired] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/photographer/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once on mount
  useEffect(() => { load(); }, []);

  // Return-from-Stripe: if we came back with ?extension=success, poll the
  // session status once to trigger the webhook fallback path, then refresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reads URL params once on mount by design
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("extension");
    const pendingId = params.get("pending_id");
    if (status === "success" && pendingId) {
      (async () => {
        try {
          const { data: p } = await api.get(`/photographer/extension-status/${pendingId}`);
          if (p.status === "consumed") {
            toast.success(`Extension paid — ${p.extended_count} photo${p.extended_count === 1 ? "" : "s"} extended`);
          } else {
            toast.info("Payment received — extension will apply momentarily.");
          }
        } catch {
          toast.info("Payment received — extension will apply momentarily.");
        }
        // Clean the URL and refresh the dashboard so the expiring list drops
        window.history.replaceState({}, "", window.location.pathname);
        load();
      })();
    } else if (status === "cancelled") {
      toast.warning("Extension payment cancelled — no photos were extended.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const extendAllExpiring = async () => {
    const ids = (data?.expiring_soon || []).map((p) => p.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Extend ${ids.length} expiring photo${ids.length === 1 ? "" : "s"} by 30 days?`)) return;
    setExtending(true);
    try {
      const { data: r } = await api.post("/photographer/extend-batch", { image_ids: ids, days: 30 });
      const paidNeeded = (r.skipped || []).filter((s) => s.reason === "payment_required");
      // Paid path: at least one photo needs payment. Send to Stripe checkout.
      if (paidNeeded.length > 0 && r.price_per_photo > 0) {
        const total = r.total_price || (r.price_per_photo * paidNeeded.length);
        const cur = (r.currency || "gbp").toUpperCase();
        const sym = cur === "GBP" ? "£" : cur === "USD" ? "$" : cur === "EUR" ? "€" : "";
        if (!window.confirm(`${paidNeeded.length} photo${paidNeeded.length === 1 ? "" : "s"} need a paid extension — total ${sym}${total.toFixed(2)}. Continue to payment?`)) {
          setExtending(false);
          return;
        }
        const { data: co } = await api.post("/photographer/extend-checkout", {
          image_ids: paidNeeded.map((s) => s.id),
          days: 30,
          origin_url: window.location.origin,
        });
        window.location.href = co.url;
        return;
      }
      if (r.extended > 0) {
        toast.success(`Extended ${r.extended} photo${r.extended === 1 ? "" : "s"} by 30 days`);
      }
      if (r.extended === 0 && (r.skipped || []).length === 0) {
        toast.info("Nothing to extend — the list may have already been refreshed.");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExtending(false);
    }
  };

  const deleteAllExpired = async () => {
    const n = data?.storage?.expired_count || 0;
    if (n === 0) return;
    if (!window.confirm(`Permanently delete ${n} expired photo${n === 1 ? "" : "s"}? The original files are removed from storage and cannot be recovered.`)) return;
    setDeletingExpired(true);
    try {
      const { data: r } = await api.post("/photographer/expired/delete-all");
      if (r.failed > 0) {
        toast.warning(`Deleted ${r.deleted}, but ${r.failed} failed — they'll be retried by the nightly sweep.`);
      } else {
        toast.success(`Deleted ${r.deleted} expired photo${r.deleted === 1 ? "" : "s"} — storage freed.`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingExpired(false);
    }
  };

  const copyHighlightsLink = async () => {
    const url = `${window.location.origin}/p/${data.public_slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied");
    } catch {
      toast.info(url);
    }
  };

  if (loading) return <p className="text-white/50 text-sm" data-testid="dashboard-loading">Loading your dashboard…</p>;
  if (!data) return <p className="text-red-400 text-sm">Failed to load dashboard.</p>;

  const cur = (data.earnings.currency || "gbp").toUpperCase();
  const sym = cur === "GBP" ? "£" : cur === "USD" ? "$" : cur === "EUR" ? "€" : "";
  const money = (n) => `${sym}${(n || 0).toFixed(2)}`;
  const storage = data.storage;
  const earn = data.earnings;
  const expiring = data.expiring_soon || [];
  const totalPhotos = storage.active_count + storage.grace_count;

  return (
    <div className="space-y-10" data-testid="photographer-dashboard">
      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          label="Live photos"
          value={totalPhotos}
          hint={storage.grace_count > 0 ? `${storage.grace_count} in grace period` : `${storage.active_count} active`}
          testid="kpi-photos"
        />
        <Kpi
          label="Storage used"
          value={`${storage.live_gb.toFixed(2)} GB`}
          hint={humanBytes(storage.active_bytes)}
          testid="kpi-storage"
        />
        <Kpi
          label="Earned (paid out)"
          value={money(earn.total_earned)}
          hint={`${earn.total_sales} total sale${earn.total_sales === 1 ? "" : "s"}`}
          testid="kpi-earned"
        />
        <Kpi
          label="Owed to you"
          value={money(earn.total_owed)}
          hint={earn.total_owed > 0 ? "Pending Stripe payout" : "Nothing outstanding"}
          highlight={earn.total_owed > 0}
          testid="kpi-owed"
        />
      </div>

      {/* Storage breakdown + recent sales */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="border border-white/10 p-6 bg-[#0a0a0a]" data-testid="storage-breakdown">
          <h3 className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold mb-4">Storage breakdown</h3>
          <div className="space-y-3 font-condensed">
            <Row label="Active photos" value={storage.active_count} />
            <Row label="In grace period" value={storage.grace_count} muted={storage.grace_count === 0} orange={storage.grace_count > 0} />
            <Row label="Expired (hidden)" value={storage.expired_count} muted={storage.expired_count === 0} />
            <Row label="Deleted" value={storage.deleted_count} muted={storage.deleted_count === 0} />
            <div className="border-t border-white/10 pt-3 mt-3 flex justify-between text-sm">
              <span className="text-white/60 uppercase tracking-[0.2em]">Storage bytes</span>
              <span className="pm-orange font-semibold">{humanBytes(storage.active_bytes)}</span>
            </div>
          </div>
          <p className="text-xs text-white/40 font-condensed mt-4 leading-relaxed">
            Free tier: first 10 GB. Photos are kept live for 30 days after upload, then move to a 7-day grace period before being hidden. You can extend individual photos from the Photos tab.
          </p>
          {storage.expired_count > 0 && (
            <button
              onClick={deleteAllExpired}
              disabled={deletingExpired}
              className="mt-4 border border-red-500 text-red-400 hover:bg-red-500 hover:text-black px-5 py-2 text-xs uppercase tracking-[0.3em] font-semibold disabled:opacity-50"
              data-testid="delete-all-expired"
            >
              {deletingExpired ? "Deleting…" : `Delete all expired · ${storage.expired_count}`}
            </button>
          )}
        </div>

        <div className="border border-white/10 p-6 bg-[#0a0a0a]" data-testid="earnings-breakdown">
          <h3 className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold mb-4">Earnings</h3>
          <div className="space-y-3 font-condensed">
            <Row label="Sales (last 30 days)" value={earn.recent_sales_30d} />
            <Row label="Total sales" value={earn.total_sales} />
            <Row label="Commission rate" value={`${Math.round((earn.commission_rate || 0.15) * 100)}%`} />
            <div className="border-t border-white/10 pt-3 mt-3 flex justify-between text-sm">
              <span className="text-white/60 uppercase tracking-[0.2em]">Paid out</span>
              <span className="text-green-400 font-semibold">{money(earn.total_earned)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-white/60 uppercase tracking-[0.2em]">Still owed</span>
              <span className={earn.total_owed > 0 ? "pm-orange font-semibold" : "text-white/40"}>{money(earn.total_owed)}</span>
            </div>
          </div>
          <p className="text-xs text-white/40 font-condensed mt-4 leading-relaxed">
            You keep {100 - Math.round((earn.commission_rate || 0.15) * 100)}% of every sale. Set up automatic Stripe payouts below to receive your share the moment a photo sells.
          </p>
        </div>
      </div>

      {/* Payouts + QR */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <ConnectCard />
        <QrCodeCard slug={data.public_slug} />
      </div>

      {/* Public highlights page */}
      {data.public_slug && (
        <div className="border border-white/10 p-6 bg-[#0a0a0a] flex flex-wrap items-center justify-between gap-4" data-testid="highlights-link-card">
          <div className="min-w-0">
            <h3 className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold mb-1">Your public highlights page</h3>
            <p className="text-sm text-white/60 font-condensed truncate">
              Share your best-selling shots with riders and fans — {window.location.origin}/p/{data.public_slug}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={copyHighlightsLink}
              className="border border-white/20 hover:border-white/60 px-5 py-2 text-xs uppercase tracking-[0.3em]"
              data-testid="copy-highlights-link"
            >
              {copied ? "Copied ✓" : "Copy link"}
            </button>
            <Link
              to={`/p/${data.public_slug}`}
              className="bg-[var(--pm-orange)] text-black px-5 py-2 text-xs uppercase tracking-[0.3em] font-bold hover:bg-[var(--pm-orange-bright)]"
              data-testid="open-highlights-link"
            >
              View page
            </Link>
          </div>
        </div>
      )}

      {/* Expiring soon */}
      <div className="border border-white/10 p-6 bg-[#0a0a0a]" data-testid="expiring-soon">
        <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
          <h3 className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">
            Expiring in the next 14 days
          </h3>
          <div className="flex items-center gap-4">
            <span className="text-xs uppercase tracking-[0.3em] text-white/40">
              {expiring.length} photo{expiring.length === 1 ? "" : "s"}
            </span>
            {expiring.length > 0 && (
              <button
                onClick={extendAllExpiring}
                disabled={extending}
                className="bg-[var(--pm-orange)] text-black px-5 py-2 text-xs uppercase tracking-[0.3em] font-bold hover:bg-[var(--pm-orange-bright)] disabled:opacity-50"
                data-testid="extend-all-expiring"
              >
                {extending ? "Extending…" : `Extend all · +30d`}
              </button>
            )}
          </div>
        </div>
        {expiring.length === 0 ? (
          <p className="text-sm text-white/50 font-condensed" data-testid="no-expiring">
            Nothing expiring soon — you&apos;re in the clear.
          </p>
        ) : (
          <ul className="divide-y divide-white/5" data-testid="expiring-list">
            {expiring.map((p) => {
              const daysLeft = Math.max(0, Math.round((new Date(p.expires_at) - new Date()) / 86400000));
              const critical = daysLeft <= 3;
              return (
                <li key={p.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Link
                      to={`/image/${p.id}`}
                      className="text-sm text-white hover:pm-orange truncate block"
                      data-testid={`expiring-title-${p.id}`}
                    >
                      {p.title}
                    </Link>
                    <p className="text-xs text-white/40 font-condensed">
                      Expires {new Date(p.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span
                    className={`text-xs uppercase tracking-[0.2em] font-semibold whitespace-nowrap ${critical ? "text-red-400" : "pm-orange"}`}
                    data-testid={`expiring-days-${p.id}`}
                  >
                    {daysLeft === 0 ? "Today" : `${daysLeft} day${daysLeft === 1 ? "" : "s"}`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, highlight, testid }) {
  return (
    <div
      className={`border-2 p-5 ${highlight ? "border-[var(--pm-orange)] bg-[#1a0a00]" : "border-white/10 bg-[#0a0a0a]"}`}
      data-testid={testid}
    >
      <p className="text-xs uppercase tracking-[0.3em] text-white/50 font-semibold">{label}</p>
      <p className="font-display text-4xl uppercase mt-2">{value}</p>
      {hint && <p className="text-xs text-white/40 font-condensed mt-1">{hint}</p>}
    </div>
  );
}

function Row({ label, value, muted, orange }) {
  return (
    <div className="flex justify-between text-sm">
      <span className={muted ? "text-white/30 uppercase tracking-[0.2em]" : "text-white/70 uppercase tracking-[0.2em]"}>{label}</span>
      <span className={orange ? "pm-orange font-semibold" : muted ? "text-white/30" : "text-white font-semibold"}>{value}</span>
    </div>
  );
}

function humanBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 2 : 1)} ${units[i]}`;
}
